import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import HuertoGrid from '../components/HuertoGrid';
import SeedModal from '../components/SeedModal';
import SeedManager from '../components/SeedManager';
import CalendarView from '../components/CalendarView';
import TareasView from '../components/TareasView';
import GastosView from '../components/GastosView';

export default function HuertoApp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados
  const [huertoConfig, setHuertoConfig] = useState(null);
  const [huertoUUID, setHuertoUUID] = useState('');
  const [huertoNombre, setHuertoNombre] = useState('Mi Huerto');
  const [customSeeds, setCustomSeeds] = useState([]);
  const [plantaciones, setPlantaciones] = useState([]);
  const [hortelanos, setHortelanos] = useState([]);
  const [currentHortelano, setCurrentHortelano] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [gastos, setGastos] = useState([]);

  // Calcular tareas pendientes hoy o vencidas
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pendingOverdueCount = tareas.filter(t => {
    if (t.realizada) return false;
    const tDate = new Date(t.fecha);
    tDate.setHours(0, 0, 0, 0);
    return tDate.getTime() <= today.getTime();
  }).length;

  // Calcular gastos pendientes del usuario actual (solo donde es moroso/deudor)
  const pendingGastosCount = currentHortelano ? gastos.filter(g =>
    !g.liquidado && g.morosos?.includes(currentHortelano.id)
  ).length : 0;

  const currentView = searchParams.get('view') || 'huerto';
  const setCurrentView = (view) => {
    setSearchParams({ view });
  };
  const [isLoading, setIsLoading] = useState(true);

  // Estado modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCaballon, setSelectedCaballon] = useState(null);

  // Inicialización y Auth
  useEffect(() => {
    checkAuthAndLoadData();
  }, [id]);

  useEffect(() => {
    if (currentHortelano && huertoUUID) {
      Sentry.setUser({
        id: currentHortelano.id,
        username: currentHortelano.nombre,
        sexo: currentHortelano.sexo,
        huerto_id: huertoUUID,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [currentHortelano, huertoUUID]);

  useEffect(() => {
    if (!huertoUUID || !huertoConfig || !currentHortelano) return;

    // Suscribirse a cambios en plantaciones
    const channel = supabase
      .channel(`public:plantaciones:huerto_id=eq.${huertoUUID}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'plantaciones',
        filter: `huerto_id=eq.${huertoUUID}`
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setPlantaciones(prev => [...prev, payload.new]);
        } else if (payload.eventType === 'UPDATE') {
          setPlantaciones(prev => prev.map(p => p.id === payload.new.id ? payload.new : p));
        } else if (payload.eventType === 'DELETE') {
          setPlantaciones(prev => prev.filter(p => p.id !== payload.old.id));
        }
      })
      .subscribe();

    // Suscribirse a cambios en semillas
    const seedsChannel = supabase
      .channel(`public:semillas_personalizadas`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'semillas_personalizadas'
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setCustomSeeds(prev => [...prev, payload.new].sort((a, b) => a.name.localeCompare(b.name)));
        } else if (payload.eventType === 'UPDATE') {
          setCustomSeeds(prev => prev.map(s => s.id === payload.new.id ? payload.new : s).sort((a, b) => a.name.localeCompare(b.name)));
        } else if (payload.eventType === 'DELETE') {
          setCustomSeeds(prev => prev.filter(s => s.id !== payload.old.id));
        }
      })
      .subscribe();

    // Suscribirse a cambios en variedades
    const varietiesChannel = supabase
      .channel('public:variedades_semillas')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'variedades_semillas'
      }, (payload) => {
        loadHuertoData(huertoUUID);
      })
      .subscribe();

    // Suscribirse a tareas
    const tareasChannel = supabase
      .channel(`public:tareas:huerto_id=eq.${huertoUUID}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tareas',
        filter: `huerto_id=eq.${huertoUUID}`
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setTareas(prev => [...prev, payload.new]);
        } else if (payload.eventType === 'UPDATE') {
          setTareas(prev => prev.map(t => t.id === payload.new.id ? payload.new : t));
        } else if (payload.eventType === 'DELETE') {
          setTareas(prev => prev.filter(t => t.id !== payload.old.id));
        }
      })
      .subscribe();

    // Suscribirse a gastos
    const gastosChannel = supabase
      .channel(`public:gastos:huerto_id=eq.${huertoUUID}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'gastos',
        filter: `huerto_id=eq.${huertoUUID}`
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setGastos(prev => [...prev, payload.new]);
        } else if (payload.eventType === 'UPDATE') {
          setGastos(prev => prev.map(g => g.id === payload.new.id ? payload.new : g));
        } else if (payload.eventType === 'DELETE') {
          setGastos(prev => prev.filter(g => g.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(seedsChannel);
      supabase.removeChannel(varietiesChannel);
      supabase.removeChannel(tareasChannel);
      supabase.removeChannel(gastosChannel);
    };
  }, [huertoUUID, huertoConfig, currentHortelano]);

  const checkAuthAndLoadData = async () => {
    setIsLoading(true);
    try {
      // 1. Cargar huerto
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      let hData = null;
      let hError = null;

      if (isUUID) {
        // Si es un UUID, buscar primero por id (clave primaria)
        const { data, error } = await supabase.from('huertos').select('*').eq('id', id).maybeSingle();
        if (data) {
          hData = data;
        } else {
          // Si no se encuentra por id, intentar por slug (en caso de que el slug tenga formato de UUID)
          const { data: slugData, error: slugError } = await supabase.from('huertos').select('*').eq('slug', id).maybeSingle();
          hData = slugData;
          hError = slugError;
        }
      } else {
        // Si no es un UUID, buscar únicamente por la columna slug
        const { data, error } = await supabase.from('huertos').select('*').eq('slug', id).maybeSingle();
        hData = data;
        hError = error;
      }

      if (hError || !hData) throw hError || new Error('Huerto no encontrado');
      
      setHuertoConfig(hData.config);
      setHuertoUUID(hData.id);
      setHuertoNombre(hData.nombre || 'Mi Huerto');

      // 2. Cargar hortelanos
      const { data: usersData, error: uError } = await supabase.from('hortelanos').select('*').eq('huerto_id', hData.id);
      if (uError) throw uError;
      setHortelanos(usersData || []);

      // 3. Comprobar Auth local
      const savedUserId = localStorage.getItem(`huerto_user_${hData.id}`);
      if (savedUserId && usersData.find(u => u.id === savedUserId)) {
        setCurrentHortelano(usersData.find(u => u.id === savedUserId));
        localStorage.setItem('last_huerto_id', id);
        await loadHuertoData(hData.id);
      }
    } catch (err) {
      console.error(err);
      alert('Huerto no encontrado.');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHuertoData = async (targetHuertoUUID) => {
    const uuidToUse = targetHuertoUUID || huertoUUID;
    if (!uuidToUse) return;
    try {
      // Semillas
      const { data: sData, error: sError } = await supabase.from('semillas_personalizadas').select('*');
      if (sError) throw sError;

      // Variedades
      const { data: vData, error: vError } = await supabase.from('variedades_semillas').select('*');
      if (vError) throw vError;

      const seedsWithVarieties = sData.map(s => {
        const seedVars = vData.filter(v => v.semilla_id === s.id).map(v => v.nombre);
        return { ...s, varieties: seedVars.length > 0 ? seedVars : undefined };
      });

      setCustomSeeds(seedsWithVarieties.sort((a, b) => a.name.localeCompare(b.name)));

      // Plantaciones
      const { data: pData, error: pError } = await supabase.from('plantaciones').select('*').eq('huerto_id', uuidToUse);
      if (pError) throw pError;
      setPlantaciones(pData || []);

      // Tareas
      const { data: tData, error: tError } = await supabase.from('tareas').select('*').eq('huerto_id', uuidToUse);
      if (tError) throw tError;
      setTareas(tData || []);

      // Gastos
      const { data: gData, error: gError } = await supabase.from('gastos').select('*').eq('huerto_id', uuidToUse);
      if (gError) throw gError;
      setGastos(gData || []);
    } catch (err) {
      console.error('Error cargando datos del huerto:', err);
    }
  };

  const handleSelectHortelano = async (user) => {
    localStorage.setItem(`huerto_user_${huertoUUID}`, user.id);
    localStorage.setItem('last_huerto_id', id);
    setCurrentHortelano(user);
    setIsLoading(true);
    await loadHuertoData(huertoUUID);
    setIsLoading(false);
  };

  // Convert plantaciones to caballonesData format for HuertoGrid
  const getCaballonesData = () => {
    if (!huertoConfig) return {};
    const data = {};
    for (let i = 0; i < huertoConfig.caballones; i++) {
      data[i] = [];
    }

    plantaciones.forEach(p => {
      const seed = customSeeds.find(s => s.id === p.seed_id);
      if (seed && data[p.caballon_index]) {
        data[p.caballon_index].push({
          id: p.id,
          seed: seed,
          variety: p.variety,
          side: p.side,
          quantity: p.quantity,
          date: p.date,
          hortelano_id: p.hortelano_id
        });
      }
    });
    return data;
  };

  const caballonesData = getCaballonesData();

  const handleCaballonClick = (cIndex, side = 'left') => {
    setSelectedCaballon({ index: cIndex, side });
    setModalOpen(true);
  };

  const handleSaveSeed = async (cIndex, data) => {
    const existing = plantaciones.find(p =>
      p.caballon_index === cIndex &&
      p.seed_id === data.seed.id &&
      (p.side || 'left') === (data.side || 'left') &&
      (p.variety || '') === (data.variety || '')
    );

    try {
      if (existing) {
        if (data.quantity === 0) {
          // Borrar
          const { error } = await supabase.from('plantaciones').delete().eq('id', existing.id);
          if (error) throw error;
        } else {
          // Actualizar
          const { error } = await supabase.from('plantaciones').update({
            quantity: data.quantity,
            date: data.date,
            hortelano_id: currentHortelano.id
          }).eq('id', existing.id);
          if (error) throw error;
        }
      } else if (data.quantity > 0) {
        // Insertar
        const { error } = await supabase.from('plantaciones').insert([{
          huerto_id: huertoUUID,
          caballon_index: cIndex,
          side: data.side || 'left',
          seed_id: data.seed.id,
          variety: data.variety || '',
          quantity: data.quantity,
          date: data.date,
          hortelano_id: currentHortelano.id
        }]);
        if (error) throw error;
      }
      setModalOpen(false);
      setSelectedCaballon(null);
    } catch (err) {
      console.error('Error guardando semilla:', err);
      alert('Error guardando la plantación');
    }
  };

  const handleRemoveSeed = async (cIndex, seedId, side, variety) => {
    const existing = plantaciones.find(p =>
      p.caballon_index === cIndex &&
      p.seed_id === seedId &&
      (p.side || 'left') === (side || 'left') &&
      (p.variety || '') === (variety || '')
    );

    if (existing) {
      try {
        const { error } = await supabase.from('plantaciones').delete().eq('id', existing.id);
        if (error) throw error;
      } catch (err) {
        console.error('Error eliminando:', err);
        alert('Error eliminando la plantación');
      }
    }
  };

  // Seed Management Handlers
  const handleAddVariety = async (seedId, varietyName) => {
    try {
      const { error } = await supabase.from('variedades_semillas').insert([{
        semilla_id: seedId,
        nombre: varietyName
      }]);
      if (error) throw error;
    } catch (err) {
      console.error(err);
      alert('Error añadiendo variedad');
    }
  };

  const handleAddCustomSeed = async (seedData) => {
    try {
      const { varieties, ...seedDataDB } = seedData;
      const newSeed = { ...seedDataDB, id: crypto.randomUUID() };
      const { error } = await supabase.from('semillas_personalizadas').insert([newSeed]);
      if (error) throw error;

      if (varieties && varieties.length > 0) {
        const varietiesToInsert = varieties.map(v => ({ semilla_id: newSeed.id, nombre: v }));
        const { error: varError } = await supabase.from('variedades_semillas').insert(varietiesToInsert);
        if (varError) throw varError;
      }
    } catch (err) {
      console.error(err);
      alert('Error añadiendo semilla');
    }
  };

  const handleUpdateCustomSeed = async (seedData) => {
    try {
      const { varieties, ...seedDataDB } = seedData;
      const { error } = await supabase.from('semillas_personalizadas').update(seedDataDB).eq('id', seedDataDB.id);
      if (error) throw error;

      await supabase.from('variedades_semillas').delete().eq('semilla_id', seedDataDB.id);

      if (varieties && varieties.length > 0) {
        const varietiesToInsert = varieties.map(v => ({ semilla_id: seedDataDB.id, nombre: v }));
        const { error: varError } = await supabase.from('variedades_semillas').insert(varietiesToInsert);
        if (varError) throw varError;
      }
    } catch (err) {
      console.error(err);
      alert('Error actualizando semilla');
    }
  };

  const handleDeleteCustomSeed = async (seedId) => {
    try {
      const { error } = await supabase.from('semillas_personalizadas').delete().eq('id', seedId);
      if (error) throw error;
      // Esto también borrará en cascada las plantaciones que referencien esta semilla?
      // Ojo: en SQL no pusimos FK de plantaciones a semillas_personalizadas, pusimos 'text' para simplificar,
      // así que deberíamos borrar las plantaciones manualmente.
      await supabase.from('plantaciones').delete().eq('seed_id', seedId).eq('huerto_id', huertoUUID);
    } catch (err) {
      console.error(err);
      alert('Error eliminando semilla');
    }
  };

  // Task Handlers
  const handleSaveTask = async (taskData) => {
    try {
      if (taskData.id) {
        const { error } = await supabase.from('tareas').update(taskData).eq('id', taskData.id);
        if (error) throw error;
      } else {
        const insertPayload = {
          huerto_id: huertoUUID,
          descripcion: taskData.descripcion,
          fecha: taskData.fecha,
          semilla_id: taskData.semilla_id,
          variedad: taskData.variedad,
          asignado_a: taskData.asignado_a
        };
        const { error } = await supabase.from('tareas').insert([insertPayload]);
        if (error) throw error;
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando tarea');
    }
  };

  const handleToggleTaskComplete = async (task, hortelanoId) => {
    try {
      const { error } = await supabase.from('tareas').update({
        realizada: !task.realizada,
        completada_por: !task.realizada ? hortelanoId : null
      }).eq('id', task.id);
      if (error) throw error;
    } catch (err) {
      console.error(err);
      alert('Error actualizando tarea');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const { error } = await supabase.from('tareas').delete().eq('id', taskId);
      if (error) throw error;
      setTareas(prev => prev.filter(t => t.id !== taskId));
    } catch (err) {
      console.error(err);
      alert('Error eliminando tarea');
    }
  };

  // Gastos Handlers
  const handleSaveGasto = async (gastoData) => {
    try {
      if (gastoData.id) {
        // Actualizar
        const { error } = await supabase.from('gastos').update({
          concepto: gastoData.concepto,
          cantidad: gastoData.cantidad,
          fecha: gastoData.fecha
        }).eq('id', gastoData.id);
        if (error) throw error;
      } else {
        // Nuevo Gasto
        const otherHortelanosIds = hortelanos
          .filter(h => h.id !== currentHortelano.id)
          .map(h => h.id);

        const insertPayload = {
          huerto_id: huertoUUID,
          concepto: gastoData.concepto,
          cantidad: gastoData.cantidad,
          fecha: gastoData.fecha,
          pagado_por: currentHortelano.id,
          morosos: otherHortelanosIds,
          liquidado: false
        };

        const { error } = await supabase.from('gastos').insert([insertPayload]);
        if (error) throw error;
      }
    } catch (err) {
      console.error('Error guardando gasto:', err);
      alert('Error guardando el gasto');
    }
  };

  const handleToggleGastoPaid = async (gastoId, isPaid) => {
    try {
      const g = gastos.find(x => x.id === gastoId);
      const payerId = g ? g.pagado_por : currentHortelano.id;

      const { error } = await supabase.from('gastos').update({
        liquidado: isPaid,
        morosos: isPaid ? [] : hortelanos.filter(h => h.id !== payerId).map(h => h.id)
      }).eq('id', gastoId);
      if (error) throw error;
    } catch (err) {
      console.error('Error actualizando estado del gasto:', err);
      alert('Error actualizando estado del gasto');
    }
  };

  const handleSettleMyPart = async (gastoId) => {
    try {
      const gasto = gastos.find(g => g.id === gastoId);
      if (!gasto || !gasto.morosos) return;

      const updatedMorosos = gasto.morosos.filter(uid => uid !== currentHortelano.id);
      const isLiquidated = updatedMorosos.length === 0;

      const { error } = await supabase.from('gastos').update({
        morosos: updatedMorosos,
        liquidado: isLiquidated
      }).eq('id', gastoId);
      if (error) throw error;
    } catch (err) {
      console.error('Error pagando parte del gasto:', err);
      alert('Error pagando tu parte del gasto');
    }
  };

  const handleDeleteGasto = async (gastoId) => {
    try {
      const { error } = await supabase.from('gastos').delete().eq('id', gastoId);
      if (error) throw error;
      setGastos(prev => prev.filter(g => g.id !== gastoId));
    } catch (err) {
      console.error('Error eliminando gasto:', err);
      alert('Error de eliminación de gasto');
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>Cargando huerto...</div>;
  }

  if (!currentHortelano) {
    // Pantalla de selección de hortelano
    return (
      <div className="animate-fade" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '400px', margin: '0 auto', width: '100%' }}>
        <div style={{ backgroundColor: 'var(--surface-color)', padding: '32px', borderRadius: 'var(--radius-lg)', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0, marginBottom: '24px' }}>¿Quién eres?</h2>
          {hortelanos.length === 0 ? (
            <p>El administrador aún no ha añadido hortelanos a este huerto.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {hortelanos.map(u => (
                <button
                  key={u.id}
                  className="btn-secondary"
                  onClick={() => handleSelectHortelano(u)}
                  style={{ padding: '16px', fontSize: '18px' }}
                >
                  {u.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'} {u.nombre}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Huerto UI
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, position: 'relative', paddingBottom: '70px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>🌱 {huertoNombre}</span>
          <span style={{ marginLeft: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>
            Hola, {currentHortelano.nombre} {currentHortelano.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'}
          </span>
        </div>
      </div>

      {currentView === 'huerto' && huertoConfig && (
        <>
          <HuertoGrid
            huerto={{ ...huertoConfig, caballonesData }}
            onCaballonClick={handleCaballonClick}
            onRemoveSeed={handleRemoveSeed}
            onReset={() => { }} // Remove local reset or add admin-only reset DB
          />
          {selectedCaballon !== null && (
            <SeedModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              onSave={handleSaveSeed}
              caballonIndex={selectedCaballon.index}
              caballonData={caballonesData[selectedCaballon.index] || []}
              longitudTotal={huertoConfig.longitud}
              availableSeeds={customSeeds}
              defaultSide={selectedCaballon.side}
            />
          )}
        </>
      )}

      {currentView === 'seeds' && (
        <SeedManager
          seeds={customSeeds}
          onAddSeed={handleAddCustomSeed}
          onUpdateSeed={handleUpdateCustomSeed}
          onDeleteSeed={handleDeleteCustomSeed}
          onAddVariety={handleAddVariety}
          onOpenCalendar={() => setCurrentView('calendar')}
          onBack={() => setCurrentView('huerto')}
        />
      )}

      {currentView === 'calendar' && (
        <CalendarView
          seeds={customSeeds}
          onBack={() => setCurrentView('seeds')}
        />
      )}

      {currentView === 'tareas' && (
        <TareasView
          tareas={tareas}
          seeds={customSeeds}
          caballonesData={caballonesData}
          hortelanos={hortelanos}
          currentHortelano={currentHortelano}
          onSaveTask={handleSaveTask}
          onToggleComplete={handleToggleTaskComplete}
          onDeleteTask={handleDeleteTask}
        />
      )}

      {currentView === 'gastos' && (
        <GastosView
          gastos={gastos}
          hortelanos={hortelanos}
          currentHortelano={currentHortelano}
          onSaveGasto={handleSaveGasto}
          onToggleGastoPaid={handleToggleGastoPaid}
          onSettleMyPart={handleSettleMyPart}
          onDeleteGasto={handleDeleteGasto}
        />
      )}

      {/* Bottom Navigation Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'var(--surface-color)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-around',
        padding: '12px 0',
        zIndex: 90
      }}>
        <button onClick={() => setCurrentView('huerto')} style={{ background: 'none', border: 'none', color: currentView === 'huerto' ? 'var(--primary-color)' : 'var(--text-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          <span style={{ fontSize: '24px' }}>🌱</span>
          <span style={{ fontSize: '12px' }}>Huerto</span>
        </button>
        <button
          onClick={() => setCurrentView('tareas')}
          style={{
            background: 'none',
            border: 'none',
            color: currentView === 'tareas' ? 'var(--primary-color)' : 'var(--text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{ fontSize: '24px' }}>📋</span>
            {pendingOverdueCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-8px',
                backgroundColor: 'var(--danger-color)',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                borderRadius: '50%',
                minWidth: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.3s ease-out forwards'
              }}>
                {pendingOverdueCount}
              </span>
            )}
          </div>
          <span style={{ fontSize: '12px' }}>Tareas</span>
        </button>
        <button
          onClick={() => setCurrentView('gastos')}
          style={{
            background: 'none',
            border: 'none',
            color: currentView === 'gastos' ? 'var(--primary-color)' : 'var(--text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{ fontSize: '24px' }}>💰</span>
            {pendingGastosCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-8px',
                backgroundColor: 'var(--danger-color)',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                borderRadius: '50%',
                minWidth: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.3s ease-out forwards'
              }}>
                {pendingGastosCount}
              </span>
            )}
          </div>
          <span style={{ fontSize: '12px' }}>Gastos</span>
        </button>
        <button onClick={() => setCurrentView('seeds')} style={{ background: 'none', border: 'none', color: currentView === 'seeds' ? 'var(--primary-color)' : 'var(--text-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          <span style={{ fontSize: '24px' }}>🌾</span>
          <span style={{ fontSize: '12px' }}>Semillas</span>
        </button>
      </div>
    </div>
  );
}
